import useFetch from '../../../hooks/useFetch';
import { useState } from 'react';
import { ServiceEvents } from '../shared/interfaces';
import { buildEndpoint } from '../EndPoints';

const useConversationService = () => {
    const { doGet, doPost } = useFetch();

    // Estados de carga
    const [fetchingConversations, setFetchingConversations] = useState(false);
    const [fetchingMessages, setFetchingMessages] = useState(false);
    const [fetchingSendMessage, setFetchingSendMessage] = useState(false);
    const [fetchingContacts, setFetchingContacts] = useState(false);
    const [fetchingUnreadCount, setFetchingUnreadCount] = useState(false);
    const [fetchingChatAI, setFetchingChatAI] = useState(false);

    const BASE_PATH = 'app/conversations';

    /**
     * Obtiene todas las conversaciones del usuario
     */
    const getConversations = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations`),
            setFetching: setFetchingConversations
        });
    };

    /**
     * Obtiene los contactos disponibles para chatear
     */
    const getAvailableContacts = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations/contacts`),
            setFetching: setFetchingContacts
        });
    };

    /**
     * Obtiene o crea una conversación con un usuario
     */
    const getConversationWithUser = (userId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations/user/${userId}`),
            setFetching: setFetchingConversations
        });
    };

    /**
     * Obtiene los mensajes de una conversación
     * @param afterId - Para polling, obtiene mensajes después de este ID
     * @param beforeId - Para paginación, obtiene mensajes antes de este ID
     */
    const getMessages = (
        conversationId: number, 
        options: { afterId?: number; beforeId?: number; limit?: number } = {},
        events: ServiceEvents = {}
    ) => {
        const params = new URLSearchParams();
        if (options.afterId) params.append('after_id', options.afterId.toString());
        if (options.beforeId) params.append('before_id', options.beforeId.toString());
        if (options.limit) params.append('limit', options.limit.toString());

        const queryString = params.toString();
        const url = `${BASE_PATH}/conversations/${conversationId}/messages${queryString ? `?${queryString}` : ''}`;

        doGet({
            ...events,
            url: buildEndpoint(url),
            setFetching: setFetchingMessages
        });
    };

    /**
     * Envía un mensaje
     */
    const sendMessage = (
        data: { conversation_id?: number; recipient_id?: number; content: string },
        events: ServiceEvents = {}
    ) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/messages/send`),
            body: data,
            setFetching: setFetchingSendMessage
        });
    };

    /**
     * Marca mensajes como leídos
     */
    const markMessagesAsRead = (conversationId: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations/${conversationId}/mark-read`),
            body: {},
            setFetching: setFetchingMessages
        });
    };

    /**
     * Obtiene el conteo de mensajes no leídos
     */
    const getUnreadCount = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations/unread-count`),
            setFetching: setFetchingUnreadCount
        });
    };

    /**
     * Envía mensaje a la IA
     * @param content - Mensaje del usuario
     * @param document - Archivo opcional para análisis legal (no se guarda)
     */
    const chatWithAI = (content: string, document?: File, events: ServiceEvents = {}) => {
        if (document) {
            const formData = new FormData();
            formData.append('content', content);
            formData.append('document', document);
            
            doPost({
                ...events,
                url: buildEndpoint(`${BASE_PATH}/chat/ai`),
                body: formData,
                requestType: 'multipart',
                setFetching: setFetchingChatAI
            });
        } else {
            doPost({
                ...events,
                url: buildEndpoint(`${BASE_PATH}/chat/ai`),
                body: { content },
                setFetching: setFetchingChatAI
            });
        }
    };

    return {
        // Estados
        fetchingConversations,
        fetchingMessages,
        fetchingSendMessage,
        fetchingContacts,
        fetchingUnreadCount,
        fetchingChatAI,
        // Métodos
        getConversations,
        getAvailableContacts,
        getConversationWithUser,
        getMessages,
        sendMessage,
        markMessagesAsRead,
        getUnreadCount,
        chatWithAI
    };
};

export default useConversationService;

